// @ts-nocheck
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import { useRegion } from "../context/RegionContext";
import "./BrandMyLaptop.css";

const AWS_LID_IMAGE = "https://home-asset.s3.eu-north-1.amazonaws.com/Brand-My-Laptop-Image-1.png";
const AWS_INSIDE_IMAGE = "https://home-asset.s3.eu-north-1.amazonaws.com/Brand-My-Laptop-Image-2.png";

interface Slot {
    slotId: string;
    section: "lid" | "palm_left" | "palm_right" | "charger" | "mouse";
    name: string;
    dimensions: string;
    price: number;
    inrPrice?: number;
    eurPrice?: number;
    currency: string;
    isBooked: boolean;
    status?: string;
    statusMessage?: string;
    sponsor?: {
        companyName?: string;
        website?: string;
        logoUrl?: string;
        bookedAt?: string;
        badgeType?: string;
    };
}

interface Campaign {
    _id: string;
    title: string;
    description: string;
    destination: string;
    startDate?: string;
    endDate?: string;
    bookingOpenDate?: string;
    bookingCloseDate?: string;
    status: "open" | "extended" | "closed" | "completed";
    isExtended: boolean;
    slots: Slot[];
    proofOfWork?: {
        photos: string[];
        recapText: string;
        publishedAt?: string;
    };
}

// Deterministic hash offset per slot (adds 5-10 for USD, 10-25 for EUR)
function getSlotOffset(slotId: string, min: number, max: number): number {
    let hash = 0;
    for (let i = 0; i < slotId.length; i++) {
        hash = (hash << 5) - hash + slotId.charCodeAt(i);
        hash |= 0;
    }
    const range = max - min + 1;
    return min + (Math.abs(hash) % range);
}

// Exact price list requested by the user:
// outside lid
// top row: 1. 2723, 2. 3645, 3. 2498
// middle row: 4. 749, 5. 598, 6. 603, 7. 812
// bottom row: 8. 1053, 9. 1121, 10. 1099
// inside laptop
// left side: 1. 142, 2. 128, 3. 139, 4. 117
// right side: 1. 153, 2. 133, 3. 136, 4. 121
const DEFAULT_SLOTS: Slot[] = [
    // Outside Lid - Top Row
    {
        slotId: "lid-1",
        section: "lid",
        name: "MacPaw",
        dimensions: "300 x 180 px",
        inrPrice: 2723,
        price: 1089,
        eurPrice: 1209,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "MacPaw", website: "https://macpaw.com", badgeType: "macpaw" }
    },
    {
        slotId: "lid-2",
        section: "lid",
        name: "see.io",
        dimensions: "320 x 180 px",
        inrPrice: 3645,
        price: 1458,
        eurPrice: 1618,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "see.io", website: "https://see.io", badgeType: "seeio" }
    },
    {
        slotId: "lid-3",
        section: "lid",
        name: "PrivateAlps",
        dimensions: "300 x 180 px",
        inrPrice: 2498,
        price: 999,
        eurPrice: 1109,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "PrivateAlps", website: "https://privatealps.com", badgeType: "privatealps" }
    },

    // Outside Lid - Middle Row
    {
        slotId: "lid-4",
        section: "lid",
        name: "Draftline Fantasy",
        dimensions: "160 x 180 px",
        inrPrice: 749,
        price: 300,
        eurPrice: 333,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Draftline Fantasy", website: "https://draftline.com", badgeType: "draftline" }
    },
    {
        slotId: "lid-4b",
        section: "lid",
        name: "SurfOffice",
        dimensions: "160 x 180 px",
        inrPrice: 598,
        price: 239,
        eurPrice: 265,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "SurfOffice", website: "https://surfoffice.com", badgeType: "surfoffice" }
    },
    {
        slotId: "lid-5",
        section: "lid",
        name: "ServicePro.co",
        dimensions: "160 x 180 px",
        inrPrice: 603,
        price: 241,
        eurPrice: 268,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "ServicePro.co", website: "https://servicepro.co", badgeType: "servicepro" }
    },
    {
        slotId: "lid-5b",
        section: "lid",
        name: "Moyai",
        dimensions: "160 x 180 px",
        inrPrice: 812,
        price: 325,
        eurPrice: 361,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Moyai", website: "https://moyai.app", badgeType: "moyai" }
    },

    // Outside Lid - Bottom Row
    {
        slotId: "lid-6",
        section: "lid",
        name: "dealhivemind.com",
        dimensions: "300 x 180 px",
        inrPrice: 1053,
        price: 421,
        eurPrice: 467,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "dealhivemind.com", website: "https://dealhivemind.com", badgeType: "dealhivemind" }
    },
    {
        slotId: "lid-7",
        section: "lid",
        name: "FELYN GO",
        dimensions: "320 x 180 px",
        inrPrice: 1121,
        price: 448,
        eurPrice: 497,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "FELYN GO", website: "https://felyngo.com", badgeType: "felyngo" }
    },
    {
        slotId: "lid-8",
        section: "lid",
        name: "nerofabric.com",
        dimensions: "300 x 180 px",
        inrPrice: 1099,
        price: 440,
        eurPrice: 488,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "nerofabric.com", website: "https://nerofabric.com", badgeType: "microplastics" }
    },

    // Inside Laptop - Left Side
    {
        slotId: "palm-left-1",
        section: "palm_left",
        name: "Sixtyfold",
        dimensions: "160 x 160 px",
        inrPrice: 142,
        price: 57,
        eurPrice: 63,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Sixtyfold", website: "https://sixtyfold.com", badgeType: "sixtyfold" }
    },
    {
        slotId: "palm-left-2",
        section: "palm_left",
        name: "QURBO",
        dimensions: "160 x 160 px",
        inrPrice: 128,
        price: 51,
        eurPrice: 57,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "QURBO", website: "https://qurbo.com", badgeType: "qurbo" }
    },
    {
        slotId: "palm-left-3",
        section: "palm_left",
        name: "DEVELOPER TIMELINE",
        dimensions: "160 x 160 px",
        inrPrice: 139,
        price: 56,
        eurPrice: 62,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "DEVELOPER TIMELINE", website: "https://developertimeline.com", badgeType: "developer" }
    },
    {
        slotId: "palm-left-4",
        section: "palm_left",
        name: "cocoonvehi...",
        dimensions: "160 x 160 px",
        inrPrice: 117,
        price: 47,
        eurPrice: 52,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "cocoonvehi...", website: "https://cocoonvehicles.co.uk", badgeType: "cocoon" }
    },

    // Inside Laptop - Right Side
    {
        slotId: "palm-right-1",
        section: "palm_right",
        name: "plan time",
        dimensions: "160 x 160 px",
        inrPrice: 153,
        price: 61,
        eurPrice: 68,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "plan time", website: "https://plantime.app", badgeType: "plantime" }
    },
    {
        slotId: "palm-right-2",
        section: "palm_right",
        name: "Product Master",
        dimensions: "160 x 160 px",
        inrPrice: 133,
        price: 53,
        eurPrice: 59,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Product Master", website: "https://productmaster.com", badgeType: "productmaster" }
    },
    {
        slotId: "palm-right-3",
        section: "palm_right",
        name: "SMALL",
        dimensions: "160 x 160 px",
        inrPrice: 136,
        price: 54,
        eurPrice: 60,
        currency: "USD",
        isBooked: false
    },
    {
        slotId: "palm-right-4",
        section: "palm_right",
        name: "Statue",
        dimensions: "160 x 160 px",
        inrPrice: 121,
        price: 48,
        eurPrice: 53,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Statue", website: "https://statue.so", badgeType: "statue" }
    },

    // Accessories
    {
        slotId: "charger-1",
        section: "charger",
        name: "Botpool",
        dimensions: "180 x 180 px",
        inrPrice: 582,
        price: 233,
        eurPrice: 259,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Botpool", website: "https://botpool.ai", badgeType: "botpool" }
    },
    {
        slotId: "mouse-1",
        section: "mouse",
        name: "Botpool",
        dimensions: "160 x 220 px",
        inrPrice: 322,
        price: 129,
        eurPrice: 143,
        currency: "USD",
        isBooked: true,
        sponsor: { companyName: "Botpool", website: "https://botpool.ai", badgeType: "botpool" }
    }
];

export default function BrandMyLaptop() {
    const [viewMode, setViewMode] = useState<"lid" | "inside">("lid");
    const [displayMode, setDisplayMode] = useState<"auction" | "final">("auction"); // 'auction' | 'final'
    const [loading, setLoading] = useState<boolean>(true);
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [pastCampaigns, setPastCampaigns] = useState<Campaign[]>([]);
    
    // Booking Modal state
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [viewingStatusSlot, setViewingStatusSlot] = useState<Slot | null>(null);
    const [companyName, setCompanyName] = useState<string>("");
    const [website, setWebsite] = useState<string>("");
    const [contactEmail, setContactEmail] = useState<string>("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>("");
    const [bookingSubmitting, setBookingSubmitting] = useState<boolean>(false);
    const [bookingSuccess, setBookingSuccess] = useState<string>("");
    const [bookingError, setBookingError] = useState<string>("");

    // Region / Currency Detection
    const region = useRegion();
    const detectedCurrency = useMemo<"INR" | "USD" | "EUR">(() => {
        const code = (region?.selectedCountry || region?.detectedCountry || "IN").toUpperCase();
        if (code === "IN") return "INR";
        if (code === "US") return "USD";
        return "EUR";
    }, [region?.selectedCountry, region?.detectedCountry]);

    const [selectedCurrency, setSelectedCurrency] = useState<"INR" | "USD" | "EUR">("INR");

    useEffect(() => {
        if (detectedCurrency) {
            setSelectedCurrency(detectedCurrency);
        }
    }, [detectedCurrency]);

    // Pricing Conversion:
    // - INR: Exact INR price provided by user
    // - USD: Converted from INR (approx inr / 2.5)
    // - EUR: 10-12% more than USD (approx usd * 1.11)
    const getSlotPriceData = (slot: Slot, currencyOverride?: "INR" | "USD" | "EUR") => {
        const defaultSlot = DEFAULT_SLOTS.find((s) => s.slotId === slot?.slotId);
        const inrAmount = slot?.inrPrice || defaultSlot?.inrPrice || Math.round((slot?.price || 50) * 2.5);
        const usdAmount = slot?.price || defaultSlot?.price || Math.round(inrAmount / 2.5);
        // EUR price is 10-12% more than USD (using 11%)
        const eurAmount = slot?.eurPrice || defaultSlot?.eurPrice || Math.round(usdAmount * 1.11);

        const curr = currencyOverride || selectedCurrency;

        if (curr === "EUR") {
            return {
                currency: "EUR",
                symbol: "€",
                amount: eurAmount,
                formatted: `€${eurAmount.toLocaleString()}`,
                inrFormatted: `₹${inrAmount.toLocaleString()}`,
                usdFormatted: `$${usdAmount.toLocaleString()}`
            };
        } else if (curr === "USD") {
            return {
                currency: "USD",
                symbol: "$",
                amount: usdAmount,
                formatted: `$${usdAmount.toLocaleString()}`,
                inrFormatted: `₹${inrAmount.toLocaleString()}`,
                eurFormatted: `€${eurAmount.toLocaleString()}`
            };
        } else {
            return {
                currency: "INR",
                symbol: "₹",
                amount: inrAmount,
                formatted: `₹${inrAmount.toLocaleString()}`,
                usdFormatted: `$${usdAmount.toLocaleString()}`,
                eurFormatted: `€${eurAmount.toLocaleString()}`
            };
        }
    };

    const fetchCurrentCampaign = async () => {
        try {
            setLoading(true);
            const res = await axios.get(API_ENDPOINTS.BRAND_MY_LAPTOP.CURRENT);
            if (res.data && res.data.success) {
                setCampaign(res.data.activeCampaign);
                setPastCampaigns(res.data.pastCampaigns || []);
            }
        } catch (err) {
            console.error("Error fetching Brand My Laptop campaign:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrentCampaign();
    }, []);

    // Helper to get slot by ID, merging active campaign data with defaults
    const getSlot = (slotId: string): Slot => {
        const fallback = DEFAULT_SLOTS.find((s) => s.slotId === slotId);
        const found = campaign?.slots?.find((s) => s.slotId === slotId);
        if (found) {
            return {
                ...fallback,
                ...found,
                inrPrice: found.inrPrice || fallback?.inrPrice,
                eurPrice: found.eurPrice || fallback?.eurPrice,
                status: found.status || (found.isBooked ? "under_review" : "available"),
                statusMessage: found.statusMessage || ""
            };
        }
        return fallback || {
            slotId,
            section: "lid",
            name: slotId,
            dimensions: "200 x 200 px",
            price: 50,
            inrPrice: 125,
            eurPrice: 56,
            currency: "USD",
            isBooked: false,
            status: "available",
            statusMessage: ""
        };
    };

    const handleSlotClick = (slot: Slot) => {
        if (slot.isBooked) {
            // If the slot has administrative status or feedback message from organizer:
            if (slot.status === "action_required" || slot.status === "rejected" || slot.statusMessage) {
                setViewingStatusSlot(slot);
                return;
            }
            if (slot.sponsor?.website) {
                let url = slot.sponsor.website;
                if (!url.startsWith("http://") && !url.startsWith("https://")) {
                    url = "https://" + url;
                }
                window.open(url, "_blank", "noopener,noreferrer");
            } else {
                setViewingStatusSlot(slot);
            }
            return;
        }
        setSelectedSlot(slot);
        setBookingSuccess("");
        setBookingError("");
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            const reader = new FileReader();
            reader.onload = (uploadEvent) => {
                setLogoPreview(uploadEvent.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBookingSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!campaign || !selectedSlot) return;

        if (!companyName.trim()) {
            setBookingError("Please enter your company name.");
            return;
        }
        if (!website.trim()) {
            setBookingError("Please enter your website URL.");
            return;
        }
        if (!contactEmail.trim()) {
            setBookingError("Please enter your email address for communication and updates.");
            return;
        }
        if (!logoFile) {
            setBookingError("Please upload your brand sticker/logo image.");
            return;
        }

        const priceData = getSlotPriceData(selectedSlot);

        setBookingSubmitting(true);
        setBookingError("");

        try {
            const formData = new FormData();
            formData.append("campaignId", campaign._id);
            formData.append("slotId", selectedSlot.slotId);
            formData.append("companyName", companyName);
            formData.append("website", website);
            formData.append("contactEmail", contactEmail);
            formData.append("logo", logoFile);
            formData.append("price", String(priceData.amount));
            formData.append("currency", priceData.currency);

            const res = await axios.post(API_ENDPOINTS.BRAND_MY_LAPTOP.BOOK, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });

            if (res.data && res.data.success) {
                setBookingSuccess(res.data.message || "Spot booked successfully!");
                await fetchCurrentCampaign();
                setTimeout(() => {
                    setSelectedSlot(null);
                    setCompanyName("");
                    setWebsite("");
                    setContactEmail("");
                    setLogoFile(null);
                    setLogoPreview("");
                    setBookingSuccess("");
                }, 2200);
            } else {
                setBookingError(res.data.message || "Could not book spot. Please try again.");
            }
        } catch (err: any) {
            console.error("Booking error:", err);
            setBookingError(err.response?.data?.message || "Something went wrong while booking.");
        } finally {
            setBookingSubmitting(false);
        }
    };

    // Calculate booked counts for Inside badge
    const insideBookedCount = useMemo(() => {
        const slots = campaign?.slots || DEFAULT_SLOTS;
        return slots.filter((s) => (s.section === "palm_left" || s.section === "palm_right") && s.isBooked).length;
    }, [campaign]);

    // Render sponsor graphic representation
    const renderSponsorGraphic = (slot: Slot) => {
        const bType = slot.sponsor?.badgeType;
        const logoUrl = slot.sponsor?.logoUrl;

        if (logoUrl) {
            const logoSrc = logoUrl.startsWith("http")
                ? logoUrl
                : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${logoUrl}`;
            return <img src={logoSrc} alt={slot.sponsor.companyName || "Sponsor"} className="bml-slot-logo-img" />;
        }

        // Exact stylized graphics from the user's reference images
        if (bType === "macpaw") {
            return (
                <div className="bml-brand-badge macpaw-badge">
                    <span className="badge-cat-ears">🐱</span>
                    <span className="badge-text-top">MacPaw</span>
                    <span className="badge-text-sub">Your Mac but better</span>
                </div>
            );
        }
        if (bType === "seeio") {
            return (
                <div className="bml-brand-badge seeio-badge">
                    <div className="seeio-eyes">
                        <span></span><span></span>
                    </div>
                    <span className="seeio-text">see.io</span>
                </div>
            );
        }
        if (bType === "privatealps") {
            return (
                <div className="bml-brand-badge privatealps-badge">
                    <div className="alps-circle">
                        <span className="alps-mountain">▲</span>
                        <span className="alps-text">PRIVATEALPS</span>
                    </div>
                </div>
            );
        }
        if (bType === "draftline") {
            return (
                <div className="bml-brand-badge draftline-badge">
                    <div className="draftline-box">
                        <span className="line-1"></span>
                        <span className="line-2"></span>
                        <span className="line-3"></span>
                    </div>
                </div>
            );
        }
        if (bType === "surfoffice") {
            return (
                <div className="bml-brand-badge surfoffice-badge">
                    <span>SurfOffice</span>
                </div>
            );
        }
        if (bType === "servicepro") {
            return (
                <div className="bml-brand-badge servicepro-badge">
                    <span className="lightning-s">S</span>
                </div>
            );
        }
        if (bType === "moyai") {
            return (
                <div className="bml-brand-badge moyai-badge">
                    <span className="moyai-icon">🗿</span>
                </div>
            );
        }
        if (bType === "dealhivemind") {
            return (
                <div className="bml-brand-badge dealhivemind-badge">
                    <span className="honeycomb">🐝</span>
                    <span>dealhivemind.com</span>
                </div>
            );
        }
        if (bType === "felyngo") {
            return (
                <div className="bml-brand-badge felyngo-badge">
                    <span className="felyn-title">FELYN</span>
                    <span className="felyn-sub">GO 🐱</span>
                </div>
            );
        }
        if (bType === "microplastics") {
            return (
                <div className="bml-brand-badge microplastics-badge">
                    <div className="micro-quote">I have microplastics in my balls</div>
                    <div className="micro-url">www.nerofabric.com</div>
                </div>
            );
        }
        if (bType === "sixtyfold") {
            return (
                <div className="bml-brand-badge sixtyfold-badge">
                    <i className="fi fi-rr-bolt" style={{ color: "#2563eb", fontSize: 16 }}></i>
                </div>
            );
        }
        if (bType === "qurbo") {
            return (
                <div className="bml-brand-badge qurbo-badge">
                    <i className="fi fi-rr-qrcode" style={{ fontSize: 18 }}></i>
                </div>
            );
        }
        if (bType === "developer") {
            return (
                <div className="bml-brand-badge developer-badge">
                    <span>{"{ }"}</span>
                </div>
            );
        }
        if (bType === "cocoon") {
            return (
                <div className="bml-brand-badge cocoon-badge">
                    <span>cocoon</span>
                </div>
            );
        }
        if (bType === "plantime") {
            return (
                <div className="bml-brand-badge plantime-badge">
                    <span>plan<br />time</span>
                </div>
            );
        }
        if (bType === "productmaster") {
            return (
                <div className="bml-brand-badge productmaster-badge">
                    <i className="fi fi-rr-portrait" style={{ fontSize: 18 }}></i>
                </div>
            );
        }
        if (bType === "statue") {
            return (
                <div className="bml-brand-badge statue-badge">
                    <span style={{ fontSize: 18 }}>🏛️</span>
                </div>
            );
        }
        if (bType === "botpool") {
            return (
                <div className="bml-brand-badge botpool-badge">
                    <i className="fi fi-rr-robot" style={{ fontSize: 14 }}></i>
                    <span>Botpool</span>
                </div>
            );
        }

        return <div className="bml-slot-logo-fallback">{slot.name.slice(0, 2).toUpperCase()}</div>;
    };

    // Render individual slot card with DOTTED BORDER AND NO BACKGROUND
    const renderSlotCard = (slotId: string, className?: string) => {
        const slot = getSlot(slotId);
        const priceData = getSlotPriceData(slot);
        const isFinal = displayMode === "final";

        if (slot.isBooked) {
            const hasNotice = slot.status === "action_required" || slot.status === "rejected" || Boolean(slot.statusMessage);
            return (
                <div
                    key={slot.slotId}
                    className={`bml-slot-item booked ${slot.status ? `status-${slot.status.replace("_", "-")}` : ""} ${isFinal ? "final-mode" : ""} ${className || ""}`}
                    onClick={() => handleSlotClick(slot)}
                    title={hasNotice ? `Status: ${(slot.status || "review").replace("_", " ")} - Click to view organizer message` : `Sponsor: ${slot.sponsor?.companyName || slot.name} - Click to visit`}
                >
                    <div className="bml-slot-logo-wrap">
                        {renderSponsorGraphic(slot)}
                    </div>
                    {!isFinal && (
                        <div className="bml-slot-info">
                            <span className="bml-slot-brand-title">{slot.sponsor?.companyName || slot.name}</span>
                            <span className="bml-slot-price-tag">{priceData.formatted}</span>
                        </div>
                    )}
                    {hasNotice && (
                        <div
                            className={`bml-slot-status-indicator indicator-${(slot.status || "action_required").replace("_", "-")}`}
                            title={slot.statusMessage ? `Organizer note: ${slot.statusMessage}` : (slot.status || "").replace("_", " ").toUpperCase()}
                        >
                            {slot.status === "rejected" ? "✕" : "!"}
                        </div>
                    )}
                </div>
            );
        }

        // Available slot
        if (isFinal) {
            return (
                <div
                    key={slot.slotId}
                    className={`bml-slot-item empty-final final-mode ${className || ""}`}
                    onClick={() => handleSlotClick(slot)}
                    title={`${slot.name} - Click to book`}
                >
                    <span className="bml-slot-subtle-empty">unsold</span>
                </div>
            );
        }

        return (
            <div
                key={slot.slotId}
                className={`bml-slot-item available ${className || ""}`}
                onClick={() => handleSlotClick(slot)}
                title={`Click to claim ${slot.name} for ${priceData.formatted}`}
            >
                <span className="bml-slot-avail-title">{slot.name}</span>
                <span className="bml-slot-avail-price">{priceData.formatted}</span>
            </div>
        );
    };

    return (
        <div className="bml-page-wrapper">
            {/* Top Center Switcher Pill: Lid vs Inside + Currency Switcher */}
            <div className="bml-top-nav-bar">
                <div className="bml-pill-switcher">
                    <button
                        className={`bml-pill-btn ${viewMode === "lid" ? "active" : ""}`}
                        onClick={() => setViewMode("lid")}
                    >
                        Lid
                    </button>
                    <button
                        className={`bml-pill-btn ${viewMode === "inside" ? "active" : ""}`}
                        onClick={() => setViewMode("inside")}
                    >
                        Inside
                        {insideBookedCount > 0 && (
                            <span className="bml-pill-count-badge">{insideBookedCount}</span>
                        )}
                    </button>
                </div>

                <div className="bml-currency-switcher">
                    <button
                        type="button"
                        className={`bml-currency-btn ${selectedCurrency === "INR" ? "active" : ""}`}
                        onClick={() => setSelectedCurrency("INR")}
                        title="Show prices in Indian Rupees (₹)"
                    >
                        ₹ INR
                    </button>
                    <button
                        type="button"
                        className={`bml-currency-btn ${selectedCurrency === "USD" ? "active" : ""}`}
                        onClick={() => setSelectedCurrency("USD")}
                        title="Show prices in US Dollars ($)"
                    >
                        $ USD
                    </button>
                    <button
                        type="button"
                        className={`bml-currency-btn ${selectedCurrency === "EUR" ? "active" : ""}`}
                        onClick={() => setSelectedCurrency("EUR")}
                        title="Show prices in Euros (€, 10-12% over USD)"
                    >
                        € EUR
                    </button>
                </div>
            </div>

            {/* Main Stage: Charger (Left) + MacBook Canvas (Center) + Magic Mouse (Right) */}
            <div className="bml-main-stage">
                {/* Left Accessory: Charger + Cable */}
                <div className="bml-accessory-column left">
                    <div className="bml-accessory-label">CHARGER + CABLE</div>
                    <div className="bml-charger-unit">
                        <div className="bml-charger-plug-prongs">
                            <span></span>
                            <span></span>
                        </div>
                        <div className="bml-charger-brick">
                            {renderSlotCard("charger-1", "bml-slot-charger")}
                        </div>
                    </div>
                </div>

                {/* Center: MacBook Canvas with AWS Photorealistic Images */}
                <div className="bml-macbook-canvas-wrapper">
                    {viewMode === "lid" ? (
                        /* ================== LID VIEW ON AWS IMAGE 1 ================== */
                        <div
                            className="bml-canvas-screen bml-canvas-lid"
                            style={{ backgroundImage: `url(${AWS_LID_IMAGE})` }}
                        >
                            {/* Top Row (3 slots) */}
                            {renderSlotCard("lid-1", "slot-overlay slot-lid-1")}
                            {renderSlotCard("lid-2", "slot-overlay slot-lid-2")}
                            {renderSlotCard("lid-3", "slot-overlay slot-lid-3")}

                            {/* Middle Row Flank Left (2 slots) */}
                            {renderSlotCard("lid-4", "slot-overlay slot-lid-4")}
                            {renderSlotCard("lid-4b", "slot-overlay slot-lid-4b")}

                            {/* Middle Row Flank Right (2 slots) */}
                            {renderSlotCard("lid-5", "slot-overlay slot-lid-5")}
                            {renderSlotCard("lid-5b", "slot-overlay slot-lid-5b")}

                            {/* Bottom Row (3 slots) */}
                            {renderSlotCard("lid-6", "slot-overlay slot-lid-6")}
                            {renderSlotCard("lid-7", "slot-overlay slot-lid-7")}
                            {renderSlotCard("lid-8", "slot-overlay slot-lid-8")}
                        </div>
                    ) : (
                        /* ================== INSIDE VIEW ON AWS IMAGE 2 ================== */
                        <div
                            className="bml-canvas-screen bml-canvas-inside"
                            style={{ backgroundImage: `url(${AWS_INSIDE_IMAGE})` }}
                        >
                            {/* Left Palm Rest (4 slots in 2x2 grid) */}
                            {renderSlotCard("palm-left-1", "slot-overlay slot-palm-l1")}
                            {renderSlotCard("palm-left-2", "slot-overlay slot-palm-l2")}
                            {renderSlotCard("palm-left-3", "slot-overlay slot-palm-l3")}
                            {renderSlotCard("palm-left-4", "slot-overlay slot-palm-l4")}

                            {/* Right Palm Rest (4 slots in 2x2 grid) */}
                            {renderSlotCard("palm-right-1", "slot-overlay slot-palm-r1")}
                            {renderSlotCard("palm-right-2", "slot-overlay slot-palm-r2")}
                            {renderSlotCard("palm-right-3", "slot-overlay slot-palm-r3")}
                            {renderSlotCard("palm-right-4", "slot-overlay slot-palm-r4")}
                        </div>
                    )}
                </div>

                {/* Right Accessory: Apple Magic Mouse */}
                <div className="bml-accessory-column right">
                    <div className="bml-accessory-label">MAGIC MOUSE</div>
                    <div className="bml-magic-mouse-unit">
                        <div className="bml-mouse-slot-wrapper">
                            {renderSlotCard("mouse-1", "bml-slot-mouse")}
                        </div>
                        <div className="bml-mouse-apple-glyph">
                            <svg viewBox="0 0 170 170" width="14" height="14" fill="currentColor">
                                <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.7-3.04-7.59-7.71-11.65-14.01-6.1-9.49-10.9-20.15-14.41-31.98-3.52-11.83-5.28-22.95-5.28-33.37 0-14.36 3.6-26.43 10.8-36.21s16.63-14.77 28.3-14.97c4.68 0 9.8 1.15 15.35 3.45 5.56 2.3 9.38 3.52 11.45 3.67 1.84-.15 5.86-1.42 12.06-3.81 6.2-2.39 11.53-3.41 15.99-3.06 12.18.98 21.84 5.38 28.98 13.2-10.68 6.42-15.92 15.42-15.72 27 .2 9.04 3.73 16.7 10.59 22.98 6.86 6.28 14.99 9.8 24.39 10.56-2.6 7.6-5.88 15.1-9.84 22.5zM119.22 33.02c0-7.44 2.66-14.46 7.98-21.06 5.32-6.6 11.95-10.92 19.89-12.96.2 1.34.3 2.58.3 3.72 0 7.33-2.77 14.42-8.31 21.27-5.54 6.85-12.28 11.08-20.22 12.69-.53-1.22-.8-2.45-.8-3.66z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Controls Pill: Live auction vs Final look */}
            <div className="bml-bottom-section">
                <div className="bml-bottom-switcher-pill">
                    <button
                        className={`bml-bot-btn ${displayMode === "auction" ? "active" : ""}`}
                        onClick={() => setDisplayMode("auction")}
                    >
                        Live auction
                    </button>
                    <button
                        className={`bml-bot-btn ${displayMode === "final" ? "active" : ""}`}
                        onClick={() => setDisplayMode("final")}
                    >
                        Final look
                    </button>
                </div>
                <div className="bml-bottom-helper-text">
                    {campaign?.status === "closed"
                        ? "The auction has closed. Tap any spot to visit the brand."
                        : "Tap any spot to claim sticker space or visit the brand."}
                </div>
            </div>

            {/* Past Proof of Work Section */}
            {pastCampaigns.length > 0 && (
                <div id="past-proof" className="bml-past-proof-section">
                    <div className="bml-past-head">
                        <h2>Previous Tours & Proof of Laptop Branding</h2>
                        <p>Real conferences, creator meetups, and developer demo days stickered with our sponsor logos.</p>
                    </div>

                    <div className="bml-past-grid">
                        {pastCampaigns.map((past) => (
                            <div key={past._id} className="bml-past-card">
                                {past.proofOfWork?.photos && past.proofOfWork.photos.length > 0 ? (
                                    <div className="bml-past-photos">
                                        {past.proofOfWork.photos.map((ph, pIdx) => (
                                            <img
                                                key={pIdx}
                                                src={ph.startsWith("http") ? ph : `${API_ENDPOINTS.AUTH.LOGIN.replace("/api/auth/login", "")}${ph}`}
                                                alt="Proof of branding"
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bml-past-placeholder-photo">
                                        <i className="fi fi-rr-laptop"></i>
                                    </div>
                                )}
                                <div className="bml-past-content">
                                    <h3>{past.title} ({past.destination})</h3>
                                    <p className="bml-past-recap">{past.proofOfWork?.recapText || past.description}</p>
                                    <div className="bml-past-sponsors-row">
                                        {past.slots
                                            .filter((s) => s.isBooked && s.sponsor?.companyName)
                                            .map((s, idx) => (
                                                <span key={idx} className="bml-past-chip">
                                                    {s.sponsor?.companyName}
                                                </span>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Booking Modal */}
            {selectedSlot && (
                <div className="bml-modal-backdrop" onClick={() => setSelectedSlot(null)}>
                    <div className="bml-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="bml-modal-close-btn" onClick={() => setSelectedSlot(null)}>✕</button>

                        <div className="bml-modal-header">
                            <h2>Claim Spot: {selectedSlot.name}</h2>
                            <p>
                                Instant spot reservation on MacBook Pro 14" • Price: <strong>{getSlotPriceData(selectedSlot).formatted}</strong>
                                <span style={{ fontSize: 12, opacity: 0.85, marginLeft: 6 }}>
                                    ({selectedCurrency === "EUR" ? `${getSlotPriceData(selectedSlot).usdFormatted} USD • ${getSlotPriceData(selectedSlot).inrFormatted} INR` : selectedCurrency === "USD" ? `${getSlotPriceData(selectedSlot).eurFormatted} EUR • ${getSlotPriceData(selectedSlot).inrFormatted} INR` : `${getSlotPriceData(selectedSlot).usdFormatted} USD • ${getSlotPriceData(selectedSlot).eurFormatted} EUR`})
                                </span>
                            </p>
                        </div>

                        {bookingSuccess ? (
                            <div className="bml-success-message">
                                <i className="fi fi-rr-check-circle" style={{ fontSize: 36, color: "#10b981", marginBottom: 12 }}></i>
                                <h3>Spot Claimed Successfully!</h3>
                                <p>{bookingSuccess}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleBookingSubmit} className="bml-booking-form">
                                {bookingError && <div className="bml-form-error">{bookingError}</div>}

                                <div className="bml-form-field">
                                    <label>Company / Brand Name *</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Acme Corp"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="bml-form-field">
                                    <label>Website URL *</label>
                                    <input
                                        type="url"
                                        placeholder="https://yourbrand.com"
                                        value={website}
                                        onChange={(e) => setWebsite(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="bml-form-field">
                                    <label>Email for Communication & Updates *</label>
                                    <input
                                        type="email"
                                        placeholder="founder@yourbrand.com"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="bml-form-field">
                                    <label>Upload Brand Sticker / Logo *</label>
                                    <input
                                        type="file"
                                        accept="image/png, image/jpeg, image/svg+xml, image/webp"
                                        onChange={handleFileChange}
                                        required
                                    />
                                    {logoPreview && (
                                        <div className="bml-logo-preview-box">
                                            <img src={logoPreview} alt="Logo preview" />
                                        </div>
                                    )}
                                </div>

                                <div className="bml-modal-footer">
                                    <button
                                        type="button"
                                        className="bml-btn-cancel"
                                        onClick={() => setSelectedSlot(null)}
                                        disabled={bookingSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="bml-btn-submit"
                                        disabled={bookingSubmitting}
                                    >
                                        {bookingSubmitting ? "Processing..." : `Claim Spot for ${getSlotPriceData(selectedSlot).formatted}`}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Status & Organizer Feedback Modal */}
            {viewingStatusSlot && (
                <div className="bml-modal-backdrop" onClick={() => setViewingStatusSlot(null)}>
                    <div className="bml-modal-content bml-status-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="bml-modal-close-btn" onClick={() => setViewingStatusSlot(null)}>✕</button>

                        <div className="bml-modal-header">
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                                <h2>{viewingStatusSlot.name}</h2>
                                <span className={`bml-status-badge badge-${(viewingStatusSlot.status || "under_review").replace("_", "-")}`}>
                                    {(viewingStatusSlot.status || "under_review").replace("_", " ").toUpperCase()}
                                </span>
                            </div>
                            <p>
                                Slot Price: <strong>{getSlotPriceData(viewingStatusSlot).formatted}</strong>
                                <span style={{ fontSize: 12, opacity: 0.85, marginLeft: 6 }}>
                                    ({selectedCurrency === "EUR" ? `${getSlotPriceData(viewingStatusSlot).usdFormatted} USD • ${getSlotPriceData(viewingStatusSlot).inrFormatted} INR` : selectedCurrency === "USD" ? `${getSlotPriceData(viewingStatusSlot).eurFormatted} EUR • ${getSlotPriceData(viewingStatusSlot).inrFormatted} INR` : `${getSlotPriceData(viewingStatusSlot).usdFormatted} USD • ${getSlotPriceData(viewingStatusSlot).eurFormatted} EUR`})
                                </span>
                            </p>
                        </div>

                        <div className="bml-status-modal-body" style={{ marginTop: 16 }}>
                            {viewingStatusSlot.sponsor?.companyName && (
                                <p style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}>
                                    Reserved by: <strong style={{ color: "#0f172a" }}>{viewingStatusSlot.sponsor.companyName}</strong>
                                </p>
                            )}

                            {viewingStatusSlot.statusMessage ? (
                                <div
                                    className="bml-status-message-box"
                                    style={{
                                        background: viewingStatusSlot.status === "rejected" ? "#fef2f2" : viewingStatusSlot.status === "action_required" ? "#fffbeb" : "#eff6ff",
                                        border: `1px solid ${viewingStatusSlot.status === "rejected" ? "#fca5a5" : viewingStatusSlot.status === "action_required" ? "#fde68a" : "#bfdbfe"}`,
                                        borderRadius: 8,
                                        padding: "14px 16px",
                                        marginBottom: 16
                                    }}
                                >
                                    <div
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            marginBottom: 6,
                                            color: viewingStatusSlot.status === "rejected" ? "#b91c1c" : viewingStatusSlot.status === "action_required" ? "#b45309" : "#1d4ed8"
                                        }}
                                    >
                                        Message from Organizer:
                                    </div>
                                    <div style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.5 }}>
                                        {viewingStatusSlot.statusMessage}
                                    </div>
                                </div>
                            ) : (
                                <p style={{ fontSize: 14, color: "#64748b", marginBottom: 16 }}>
                                    {viewingStatusSlot.status === "under_review"
                                        ? "This slot has been booked and is currently under review by the host."
                                        : viewingStatusSlot.status === "accepted"
                                        ? "This slot has been accepted and confirmed for this event."
                                        : "This slot is currently reserved."}
                                </p>
                            )}

                            {viewingStatusSlot.sponsor?.website && (
                                <div style={{ marginTop: 12 }}>
                                    <a
                                        href={viewingStatusSlot.sponsor.website.startsWith("http") ? viewingStatusSlot.sponsor.website : `https://${viewingStatusSlot.sponsor.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#2563eb", fontWeight: 600, textDecoration: "none", fontSize: 14 }}
                                    >
                                        Visit Sponsor Website →
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="bml-modal-footer" style={{ marginTop: 24, justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className="bml-btn-cancel"
                                onClick={() => setViewingStatusSlot(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
